/**
 * A catalog item keeps its stages as names (`stages`) and, beside them, the
 * names of the stages that release inventory products when the work starts
 * (`releaseStages`). The form edits the flag per row, so these convert
 * between the two shapes.
 */

const clean = (value: unknown) => String(value ?? "").trim();

/** One flag per stage row, from the stored name list. */
export function releaseFlagsFor(stages: unknown[], releaseStages: unknown): boolean[] {
  const flagged = new Set((Array.isArray(releaseStages) ? releaseStages : []).map(clean));
  return (Array.isArray(stages) ? stages : []).map((s) => {
    const name = clean(s);
    return !!name && flagged.has(name);
  });
}

/** The names to send: flagged rows that have a name, in row order. */
export function flaggedStageNames(stages: string[], flags: boolean[]): string[] {
  return stages.map((s, i) => (flags[i] ? clean(s) : "")).filter(Boolean);
}

/** True when `stage` releases products on this catalog item. */
export function stageReleasesProducts(item: { releaseStages?: unknown } | null | undefined, stage: unknown): boolean {
  const name = clean(stage);
  return !!name && (Array.isArray(item?.releaseStages) ? item!.releaseStages : []).some((s) => clean(s) === name);
}

/**
 * A flagged stage may be tied to one inventory category and some of its sub
 * categories (`stageCategories` on the catalog item). The sheet then offers
 * only those products for the stage. One link (or null) per stage row.
 */
export type StageLink = { categoryId: string; subCategoryIds: string[] } | null;

const idOf = (value: any) => String(value?._id ?? value ?? "").trim();

/** One link per stage row, from the stored list. */
export function stageLinksFor(stages: unknown[], stageCategories: unknown): StageLink[] {
  const list: any[] = Array.isArray(stageCategories) ? stageCategories : [];
  return (Array.isArray(stages) ? stages : []).map((s) => {
    const name = clean(s);
    const hit = name ? list.find((l) => clean(l?.stage) === name) : null;
    if (!hit || !idOf(hit.categoryId)) return null;
    const subs = Array.isArray(hit.subCategoryIds) ? hit.subCategoryIds.map(idOf).filter(Boolean) : [];
    return { categoryId: idOf(hit.categoryId), subCategoryIds: subs };
  });
}

/** The list to send: flagged, named rows that have a category, in row order. */
export function stageCategoriesPayload(stages: string[], flags: boolean[], links: StageLink[]) {
  return stages
    .map((s, i) => ({ stage: clean(s), link: flags[i] ? links[i] : null }))
    .filter((row) => row.stage && row.link?.categoryId)
    .map((row) => ({
      stage: row.stage,
      categoryId: row.link!.categoryId,
      subCategoryIds: row.link!.subCategoryIds,
    }));
}

/** Active sub categories of a category, A–Z. */
export function subCategoriesOf(subCategories: any[], categoryId: string): any[] {
  if (!categoryId) return [];
  return (subCategories || [])
    .filter((s) => s?.status !== false && idOf(s?.categoryId) === categoryId)
    .sort((a, b) => String(a.subCategoryName || "").localeCompare(String(b.subCategoryName || "")));
}

/**
 * Rows that can't be saved yet: flagged, with a category that has sub
 * categories, but none of them ticked.
 */
export function stageLinksMissingSubs(
  stages: string[],
  flags: boolean[],
  links: StageLink[],
  subCategories: any[]
): number[] {
  return stages
    .map((s, i) => {
      const link = links[i];
      if (!clean(s) || !flags[i] || !link?.categoryId) return -1;
      const own = subCategoriesOf(subCategories, link.categoryId).map((sub) => idOf(sub._id));
      if (!own.length) return -1;
      return link.subCategoryIds.some((id) => own.includes(id)) ? -1 : i;
    })
    .filter((i) => i !== -1);
}
